import { createContext, useCallback, useContext, useRef, useState, useSyncExternalStore } from "react";

export const ScreenStateContext = createContext(null);
const empty = {};
const subscribeNone = () => () => {};
const emptySnapshot = () => empty;

// Each caller supplies a small schema via defaults; unregistered fields are dropped.
export function useScreenState(scope, defaults, validate = (value) => value) {
  const store = useContext(ScreenStateContext);
  const [local, setLocal] = useState(defaults);
  const fallback = useRef(local);
  const snapshot = useSyncExternalStore(store?.subscribe || subscribeNone,
    store?.getSnapshot || emptySnapshot, emptySnapshot);
  const normalise = (raw) => {
    const merged = { ...fallback.current };
    for (const key of Object.keys(merged)) {
      if (raw && Object.hasOwn(raw, key) &&
        (raw[key] === null || typeof raw[key] === typeof merged[key] ||
          (merged[key] === null && ["string", "number", "boolean"].includes(typeof raw[key])))) merged[key] = raw[key];
    }
    return validate(merged);
  };
  const current = useRef(null);
  current.current = { store, scope, normalise };
  const setValue = useCallback((next) => {
    const state = current.current;
    if (!state.store) {
      setLocal((previous) => state.normalise(typeof next === "function"
        ? next(state.normalise(previous)) : next));
      return;
    }
    const previous = state.normalise(state.store.read(state.scope));
    const value = typeof next === "function" ? next(previous) : next;
    state.store.write(state.scope, state.normalise(value));
  }, []);
  return [normalise(store ? snapshot[scope] : local), setValue];
}
